import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Tabs,
  Tab,
  IconButton,
  Typography,
  Chip,
  Stack,
  Paper,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import closeicon from "../assets/images/closeicon.png";
import DynamicBarChart from "./DynamicBarChart";
import MultiTableView from "./MultiTableView";

const ReportDialog = ({
  open,
  handleClose,
  toolResults,
  additional_agent_responses,
  reformatted_question,
}) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  const hasSourceInsights = Object.keys(additional_agent_responses || {}).length > 1;

  useEffect(() => {
    if (!open) {
      setTabValue(0);
      return;
    }
    if (!hasSourceInsights && tabValue > 1) {
      setTabValue(0);
    }
  }, [open, hasSourceInsights, tabValue]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const toPascalCase = (str) =>
    str.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  const getTabLabel = () => {
    if (tabValue === 0) return "Chart View";
    if (tabValue === 1) return "Table View";
    return "Source Insights";
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "920px",
          width: "92%",
          display: "flex",
          flexDirection: "column",
          borderRadius: "18px",
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(165deg, rgba(17,24,39,0.98) 0%, rgba(15,23,42,0.98) 100%)"
              : "linear-gradient(165deg, #FFFFFF 0%, #F8FBFF 100%)",
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        },
      }}
    >
      <DialogTitle sx={{ px: 3, py: 2.2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.45rem", color: "text.primary", lineHeight: 1.15 }}>
              Detailed View
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: "0.82rem", color: "text.secondary" }}>
              Explore charts, table outputs, and source insights for the selected response.
            </Typography>
          </Box>

          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              boxShadow: "0 4px 14px rgba(15,23,42,0.08)",
            }}
          >
            <img src={closeicon} style={{ width: "18px" }} alt="close" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <Box sx={{ width: "100%", px: 2.5, pt: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.8 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="report tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 46,
              "& .MuiTab-root": {
                minHeight: 46,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.88rem",
                color: "text.secondary",
                px: 1.5,
              },
              "& .Mui-selected": {
                color: "#1E3A8A !important",
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "999px",
                backgroundColor: "#2563EB",
              },
            }}
          >
            <Tab label="Chart View" />
            <Tab label="Table View" />
            {hasSourceInsights && <Tab label="Source Insights" />}
          </Tabs>

          <Chip
            size="small"
            label={getTabLabel()}
            sx={{
              fontWeight: 600,
              borderRadius: "999px",
              color: "#1E3A8A",
              backgroundColor: alpha("#2563EB", 0.1),
            }}
          />
        </Stack>

        <Divider />
      </Box>

      <DialogContent sx={{ px: 2.5, py: 2, overflowY: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: "550px",
            p: { xs: 1.5, md: 2 },
            borderRadius: "14px",
            border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
            backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.18 : 0.8),
          }}
        >
          {tabValue === 0 && (
            <Box>
              <DynamicBarChart toolResults={toolResults || []} />
            </Box>
          )}

          {tabValue === 1 && (
            <Box>
              <MultiTableView
                toolResults={toolResults || []}
                reformatted_question={reformatted_question}
              />
            </Box>
          )}

          {tabValue === 2 && hasSourceInsights && (
            <Box>
              {Object.keys(additional_agent_responses || {}).map((key) => (
                <Paper
                  key={key}
                  elevation={0}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: "12px",
                    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                    backgroundColor: alpha(
                      theme.palette.background.paper,
                      theme.palette.mode === "dark" ? 0.16 : 0.9
                    ),
                  }}
                >
                  <Typography
                    sx={{
                      mb: 1,
                      fontWeight: 700,
                      color: "text.primary",
                      fontSize: "0.96rem",
                    }}
                  >
                    {toPascalCase(key)}
                  </Typography>
                  <Typography
                    sx={{
                      mb: 0.5,
                      color: "text.secondary",
                      fontSize: "0.92rem",
                      lineHeight: 1.6,
                      "& h1, & h2": {
                        fontSize: "1rem",
                        fontWeight: 700,
                        margin: "8px 0",
                        color: "text.primary",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: additional_agent_responses[key]
                        ? additional_agent_responses[key]
                            .replace(/^\s*### (.*)$/gm, "<h1>$1</h1>")
                            .replace(/^\s*## (.*)$/gm, "<h1>$1</h1>")
                            .replace(/^\s*# (.*)$/gm, "<h1>$1</h1>")
                            .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
                        : "Not available.",
                    }}
                  ></Typography>
                </Paper>
              ))}
            </Box>
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

ReportDialog.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  toolResults: PropTypes.array,
  additional_agent_responses: PropTypes.object,
  reformatted_question: PropTypes.string,
};

export default ReportDialog;
